import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function OpeningDetailPage() {
  const { id } = useParams();
  const [opportunity, setOpportunity] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/opportunities/${id}`)
      .then((r) => r.json())
      .then((json) => setOpportunity(json.success ? json.data : null));

    fetch(`${API_BASE}/opportunities/${id}/related`)
      .then((r) => r.json())
      .then((json) => setRelated(json.success ? json.data : []))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading...</p>;
  if (!opportunity) return <p className="text-center py-40 text-[#64748b]">Opportunity not found.</p>;

  const isJob = opportunity.type === 'JOB';
  const feeBreakdown = opportunity.feeBreakdown || {};
  const stages = feeBreakdown.stages || [];

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-20 max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 mb-6 text-sm">
          <Link className="text-[#64748b] hover:text-[#00255a] flex items-center gap-1" to="/">
            <span className="material-symbols-outlined text-[18px]">home</span> Home
          </Link>
          <span className="material-symbols-outlined text-[#64748b] text-[16px]">chevron_right</span>
          <Link className="text-[#64748b] hover:text-[#00255a]" to="/openings">Openings</Link>
          <span className="material-symbols-outlined text-[#64748b] text-[16px]">chevron_right</span>
          <span className="text-[#00255a] font-semibold">{opportunity.title}</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="space-y-4">
            <span className="inline-flex items-center gap-2 bg-[#8fb3ff]/20 text-[#174388] px-3 py-1 rounded-full text-sm">
              <span className="w-2 h-2 rounded-full bg-[#8fb3ff]" />
              {opportunity.status === 'OPEN' ? 'Open for Applications' : 'Closed'}
            </span>
            <h1 className="text-4xl font-bold text-[#00255a] max-w-4xl">{opportunity.title}</h1>
            <div className="flex flex-wrap gap-4 items-center text-[#64748b]">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#00255a]">location_on</span>
                <span>{opportunity.country}</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-[#c4c6d1]" />
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#00255a]">{isJob ? 'work' : 'school'}</span>
                <span>{opportunity.studyLevel || opportunity.sector || opportunity.type}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-3">
            <div className="text-[#64748b] text-sm">
              {opportunity.paymentModel === 'LUMP_SUM' ? 'One-Time Payment' : 'Starting From'}
            </div>
            <div className="text-3xl font-bold text-[#00255a]">
              {opportunity.feeCurrency} {opportunity.feeFromAmount || opportunity.salaryFrom}
            </div>
            <Link
              to={`/apply/${opportunity.type === 'JOB' ? 'job' : 'study'}?opportunityId=${opportunity.id}`}
              className="text-white px-8 py-4 rounded-xl font-semibold shadow-xl hover:scale-[1.05] transition-all flex items-center gap-2 w-full lg:w-auto justify-center"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Secure Your Spot
              <span className="material-symbols-outlined">arrow_forward</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main content */}
          <div className="lg:col-span-8 space-y-12">
            <div className="rounded-2xl overflow-hidden h-[400px] relative">
              <img
                className="absolute inset-0 w-full h-full object-cover"
                alt={opportunity.country}
                src={opportunity.imageUrl || `/images/countries/${opportunity.country.toLowerCase().replace(/\s+/g, '-')}.jpg`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#00255a]/60 to-transparent" />
              {opportunity.institution && (
                <div className="absolute bottom-6 left-6 bg-white/20 backdrop-blur-md p-4 rounded-xl border border-white/30 text-white">
                  <p className="text-sm opacity-80 mb-1">Affiliated Institution</p>
                  <p className="text-xl font-bold">{opportunity.institution.name}</p>
                </div>
              )}
            </div>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-[#00255a]">Program Overview</h2>
              <p className="text-[#44474f] leading-relaxed">{opportunity.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(opportunity.perks || []).map((perk, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#e2e8f0]">
                    <span className="material-symbols-outlined text-[#ff8483]">check_circle</span>
                    <p className="text-[#44474f]">{perk}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Fee Breakdown */}
            <section className="bg-[#00255a] text-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-xl font-bold mb-6">Fee Breakdown</h3>
              <div className="space-y-4">
                {opportunity.paymentModel === 'LUMP_SUM' ? (
                  <div className="flex justify-between items-center py-4 text-[#ff8483]">
                    <span className="text-lg font-bold">One-Time Total Payment</span>
                    <span className="text-lg font-bold">{feeBreakdown.currency} {feeBreakdown.totalAmount}</span>
                  </div>
                ) : (
                  stages.map((s, i) => (
                    <div key={i} className="flex justify-between items-center py-3 border-b border-white/10">
                      <span className="opacity-80">{s.label}</span>
                      <span className="font-semibold">${s.amount}</span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-2xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#f2f4f6] flex items-center justify-center text-[#00255a]">
                  <span className="material-symbols-outlined">public</span>
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Country</p>
                  <p className="font-semibold">{opportunity.country}</p>
                </div>
              </div>
              {opportunity.deadline && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#f2f4f6] flex items-center justify-center text-[#00255a]">
                    <span className="material-symbols-outlined">event</span>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Application Deadline</p>
                    <p className="font-semibold">{new Date(opportunity.deadline).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-xl border-2 border-[#1e3c72] p-6">
              <h4 className="text-xl font-bold text-[#00255a] mb-2">Need Help?</h4>
              <p className="text-[#64748b] mb-6">Talk to an agent for guidance on this opportunity.</p>
              <a
                href="https://wa.me/263775827371"
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-3 rounded-lg flex items-center justify-center gap-2 font-semibold transition-all shadow-md"
              >
                WhatsApp an Agent
              </a>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-24">
            <div className="flex justify-between items-end mb-8">
              <h2 className="text-2xl font-bold text-[#00255a]">Similar Opportunities</h2>
              <Link to="/openings" className="text-[#00255a] font-semibold border-b-2 border-[#00255a] pb-1">View All</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/openings/${r.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#e2e8f0] group hover:shadow-xl transition-all">
                  <div className="h-48 relative overflow-hidden">
                    <img
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      alt={r.country}
                      src={r.imageUrl || `/images/countries/${r.country.toLowerCase().replace(/\s+/g, '-')}.jpg`}
                    />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[12px] font-bold text-[#00255a]">
                      {r.country.toUpperCase()}
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="font-semibold text-[#00255a] mb-2 line-clamp-1">{r.title}</h4>
                    <div className="flex justify-between items-center">
                      <span className="text-lg text-[#00255a]">{r.feeCurrency} {r.feeFromAmount || r.salaryFrom}</span>
                      <span className="material-symbols-outlined text-[#00255a] group-hover:translate-x-1 transition-transform">arrow_forward</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
